/**
 * Pure mutation functions for clipboard actions.
 */

import {PcbTreeManager, setElementsAndGenerateNodes} from "@buildwithflux/core";
import type {
    AnyPcbNode,
    IDocumentData,
    IElementData,
    IPropertiesMap,
    IVertexMap,
    PcbNodesMap,
    PcbRulesMap,
    RuleKey,
} from "@buildwithflux/models";
import {isEmpty} from "lodash";

import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

/**
 * Pure mutation: pastes properties from clipboard to target elements/routes/document.
 */
export function applyAddSubjectsPropertiesFromClipboard(
    draft: IDocumentData,
    targetElementUids: string[],
    targetRouteUids: string[],
    properties: IPropertiesMap,
): void {
    for (const elementUid of targetElementUids) {
        for (const [propertyUid, property] of Object.entries(properties)) {
            const element = draft.elements[elementUid];
            if (element) {
                const existingPropertyWithSameName = Object.values(element.properties).find(
                    (existingProperty) => existingProperty.name === property.name,
                );

                if (existingPropertyWithSameName) {
                    element.properties[existingPropertyWithSameName.uid] = {
                        ...property,
                        uid: existingPropertyWithSameName.uid,
                    };
                } else {
                    element.properties[propertyUid] = property;
                }
            }
        }
    }

    for (const routeUid of targetRouteUids) {
        for (const [propertyUid, property] of Object.entries(properties)) {
            const route = draft.routes[routeUid];
            if (route) {
                const existingPropertyWithSameName = Object.values(route.properties).find(
                    (existingProperty) => existingProperty.name === property.name,
                );

                if (existingPropertyWithSameName) {
                    route.properties[existingPropertyWithSameName.uid] = {
                        ...property,
                        uid: existingPropertyWithSameName.uid,
                    };
                } else {
                    route.properties[propertyUid] = property;
                }
            }
        }
    }

    if (targetElementUids.length === 0 && targetRouteUids.length === 0) {
        for (const [propertyUid, property] of Object.entries(properties)) {
            const existingPropertyWithSameName = Object.values(draft.properties).find(
                (existingProperty) => existingProperty.name === property.name,
            );

            if (existingPropertyWithSameName) {
                draft.properties[existingPropertyWithSameName.uid] = {
                    ...property,
                    uid: existingPropertyWithSameName.uid,
                };
            } else {
                draft.properties[propertyUid] = property;
            }
        }
    }
}

/**
 * Pure mutation: pastes PCB layout rules from clipboard to target nodes/rulesets.
 */
export function applyAddGlobalRulesFromClipboard(
    draft: IDocumentData,
    targetObjectUids: string[],
    nodeLayoutRules: PcbRulesMap,
): void {
    for (const objectUid of targetObjectUids) {
        for (const [layoutRuleUid, layoutRule] of Object.entries(nodeLayoutRules)) {
            const node = draft.pcbLayoutNodes[objectUid];
            if (node) {
                node.pcbNodeRuleSet[layoutRuleUid as RuleKey] = layoutRule;
            } else {
                const ruleSet = draft.pcbLayoutRuleSets[objectUid];
                if (ruleSet) {
                    ruleSet.rules[layoutRuleUid as RuleKey] = layoutRule;
                }
            }
        }
    }
}

/**
 * Pure mutation: pastes elements and route vertices from clipboard.
 */
export function applyAddSubjectsFromClipboard(
    draft: IDocumentData,
    elements: IElementData[],
    routeVertices: IVertexMap,
): void {
    const draftElements = draft.elements;
    const draftRouteVertices = draft.routeVertices;

    for (const element of elements) {
        draftElements[element.uid] = element;
    }

    for (const [uid, routeVertex] of Object.entries(routeVertices)) {
        draftRouteVertices[uid] = routeVertex;
    }

    setElementsAndGenerateNodes(draft, Object.values(elements));

    const pcbTreeManager = new PcbTreeManager(draft.pcbLayoutNodes, FluxLogger);
    pcbTreeManager.updateNetNodes(draft);
    draft.pcbLayoutNodes = pcbTreeManager.treeNodes as PcbNodesMap<AnyPcbNode>;
}
